import jwtDecode from "jwt-decode";
import React, { createContext, useContext, useEffect, useState } from "react";
import {
  Inaction,
  ProcessoInAction,
  Redistribution,
} from "../api/services/attorneys/attorneys.interface";
import { GetProfileMenusResponse } from "../api/services/profile/profile.interface";
import { getUser } from "../api/services/users/users";
import { User } from "../api/services/users/users.interface";
import { decryptData } from "../utils/token.util";
import { PaginationProps } from "./interfaces/pagination.interface";
import { SelectedUserProps } from "./interfaces/selected-user.interface";
import {
  RegisterActOriginProcessData,
  SharedStateContextProps,
} from "./interfaces/shared-state.interface";
import { UserProps } from "./interfaces/user.interface";

const MyContext = createContext<SharedStateContextProps>({
  user: null,
  setUser: () => {},
  userDetails: null,
  setUserDetails: () => {},
  userPosition: null,
  setUserPosition: () => {},
  selectedUser: null,
  setSelectedUser: () => {},
  homeLink: null,
  setHomeLink: () => {},
  menu: {} as GetProfileMenusResponse,
  setMenu: () => {},
  pagination: {} as PaginationProps,
  setPagination: () => {},
  selectedDataTable: [],
  setSelectedDataTable: () => {},
  selectedProcessoInActionDataTable: [],
  setSelectedProcessoInActionDataTable: () => {},
  selectedInactionDataTable: [],
  setSelectedInactionDataTable: () => {},
  selectedRedistributionDataTable: [],
  setSelectedRedistributionDataTable: () => {},
  selectedRowHashes: [],
  setSelectedRowHashes: () => {},
  selectedAttorneyByGeneralAttorneyDashboard: null,
  setSelectedAttorneyByGeneralAttorneyDashboard: () => {},
  countProcessDeadlineSchedule: {
    deadline_today: 0,
    deadline_tomorrow: 0,
    deadline_week: 0,
  },
  setCountProcessDeadlineSchedule: () => {},
  registerActOriginProcessData: null,
  setRegisterActOriginProcessData: () => {},
});

export const UseSharedState = ({ children }: { children: React.ReactNode }) => {
  const token = localStorage?.getItem("token");
  const defaultUser: UserProps | null = token ? jwtDecode(token) : null;
  const defaultSelectedUser = localStorage?.getItem("selectedUser");
  const defaultSelected = defaultSelectedUser
    ? decryptData(defaultSelectedUser)
    : null;
  const [user, setUser] = useState<UserProps | null>(defaultUser);
  const [userDetails, setUserDetails] = useState<User | null>(null);
  const [userPosition, setUserPosition] = useState<string>(
    localStorage?.getItem("userPosition"),
  );
  const [selectedUser, setSelectedUser] = useState<SelectedUserProps | null>(
    defaultSelected,
  );
  const homePage = localStorage?.getItem("homePage");
  const [homeLink, setHomeLink] = useState<string | null>(homePage);

  const [menu, setMenu] = useState<GetProfileMenusResponse>(
    {} as GetProfileMenusResponse,
  );

  const [pagination, setPagination] = useState<PaginationProps>(
    {} as PaginationProps,
  );
  const [selectedRowHashes, setSelectedRowHashes] = useState<number[]>([]);
  const [selectedDataTable, setSelectedDataTable] = useState<any[]>([]);
  const [
    selectedProcessoInActionDataTable,
    setSelectedProcessoInActionDataTable,
  ] = useState<ProcessoInAction[]>([]);
  const [selectedInactionDataTable, setSelectedInactionDataTable] = useState<
    Inaction[]
  >([]);
  const [selectedRedistributionDataTable, setSelectedRedistributionDataTable] =
    useState<Redistribution[]>([]);
  const [
    selectedAttorneyByGeneralAttorneyDashboard,
    setSelectedAttorneyByGeneralAttorneyDashboard,
  ] = useState(null);
  const [countProcessDeadlineSchedule, setCountProcessDeadlineSchedule] =
    useState({
      deadline_today: 0,
      deadline_tomorrow: 0,
      deadline_week: 0,
    });
  const [registerActOriginProcessData, setRegisterActOriginProcessData] =
    useState<RegisterActOriginProcessData | null>(null);

  useEffect(() => {
    const userId = user?.["Jvris.User.Id"];
    const pathname = window.location.pathname;
    let isMounted = true;

    if (!userId) {
      setUserDetails(null);
      return;
    }

    if (pathname === "/" || pathname === "/not-authorized") {
      return;
    }

    const loadUserDetails = async () => {
      try {
        const response = await getUser(+userId);

        if (isMounted) {
          setUserDetails(response?.data ?? null);
        }
      } catch (error) {
        if (isMounted) {
          setUserDetails(null);
        }
      }
    };

    loadUserDetails();

    return () => {
      isMounted = false;
    };
  }, [user]);

  return (
    <MyContext.Provider
      value={{
        user,
        setUser,
        userDetails,
        setUserDetails,
        userPosition,
        setUserPosition,
        selectedUser,
        setSelectedUser,
        homeLink,
        setHomeLink,
        menu,
        setMenu,
        pagination,
        setPagination,
        selectedDataTable,
        setSelectedDataTable,
        selectedProcessoInActionDataTable,
        setSelectedProcessoInActionDataTable,
        selectedInactionDataTable,
        setSelectedInactionDataTable,
        selectedRedistributionDataTable,
        setSelectedRedistributionDataTable,
        selectedRowHashes,
        setSelectedRowHashes,
        selectedAttorneyByGeneralAttorneyDashboard,
        setSelectedAttorneyByGeneralAttorneyDashboard,
        countProcessDeadlineSchedule,
        setCountProcessDeadlineSchedule,
        registerActOriginProcessData,
        setRegisterActOriginProcessData,
      }}
    >
      {children}
    </MyContext.Provider>
  );
};

export const SharedState = () => {
  const context = useContext(MyContext);
  if (!context) {
    console.error("Context is not defined");
  }
  const {
    user,
    setUser,
    userDetails,
    setUserDetails,
    userPosition,
    setUserPosition,
    selectedUser,
    setSelectedUser,
    homeLink,
    setHomeLink,
    menu,
    setMenu,
    pagination,
    setPagination,
    selectedDataTable,
    setSelectedDataTable,
    selectedProcessoInActionDataTable,
    setSelectedProcessoInActionDataTable,
    selectedInactionDataTable,
    setSelectedInactionDataTable,
    selectedRedistributionDataTable,
    setSelectedRedistributionDataTable,
    selectedRowHashes,
    setSelectedRowHashes,
    selectedAttorneyByGeneralAttorneyDashboard,
    setSelectedAttorneyByGeneralAttorneyDashboard,
    countProcessDeadlineSchedule,
    setCountProcessDeadlineSchedule,
    registerActOriginProcessData,
    setRegisterActOriginProcessData,
  } = context;
  return {
    user,
    setUser,
    userDetails,
    setUserDetails,
    userPosition,
    setUserPosition,
    selectedUser,
    setSelectedUser,
    homeLink,
    setHomeLink,
    menu,
    setMenu,
    pagination,
    setPagination,
    selectedDataTable,
    setSelectedDataTable,
    selectedProcessoInActionDataTable,
    setSelectedProcessoInActionDataTable,
    selectedInactionDataTable,
    setSelectedInactionDataTable,
    selectedRedistributionDataTable,
    setSelectedRedistributionDataTable,
    selectedRowHashes,
    setSelectedRowHashes,
    selectedAttorneyByGeneralAttorneyDashboard,
    setSelectedAttorneyByGeneralAttorneyDashboard,
    countProcessDeadlineSchedule,
    setCountProcessDeadlineSchedule,
    registerActOriginProcessData,
    setRegisterActOriginProcessData,
  };
};
